import React, { useState, useEffect, useRef } from 'react';
import { Box, Select, MenuItem, FormControl, useTheme, Menu, ListItemIcon, ListItemText, Tabs, Tab, TextField, InputAdornment, IconButton, alpha } from '@mui/material';
import { Quill } from 'react-quill';
import ToolbarButton, { ToolbarDivider, ToolbarGroup } from './ToolbarButton';
import ColorPicker from './ColorPicker';
import ButtonDialog from './ButtonDialog';
import InputDialog from './InputDialog';
import { getRecentEmojis, addRecentEmoji } from '../../services/pdfService';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentDecrease,
  FormatIndentIncrease,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatQuote,
  Code,
  Link,
  Image,
  VideoLibrary,
  FormatClear,
  FormatColorText,
  FormatColorFill,
  Superscript,
  Subscript,
  SmartButton,
  Input,
  Upload as UploadIcon,
  EmojiEmotions as EmojiIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  SplitscreenOutlined as PageBreakIcon,
} from '@mui/icons-material';

const CustomQuillToolbar = ({ quillRef, activeFormats = {}, pdfType = 'simple' }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [currentFormats, setCurrentFormats] = useState(activeFormats);
  const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [editingButtonNode, setEditingButtonNode] = useState(null);
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [editingInput, setEditingInput] = useState(null);
  const [editingInputNode, setEditingInputNode] = useState(null);
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [emojiMenuAnchor, setEmojiMenuAnchor] = useState(null);
  const [emojiTab, setEmojiTab] = useState(0);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [recentEmojis, setRecentEmojis] = useState([]);
  const [lastSelection, setLastSelection] = useState(null);

  // Load recent emojis from server on mount
  useEffect(() => {
    const fetchRecentEmojis = async () => {
      try {
        const response = await getRecentEmojis();
        if (response && response.data) {
          setRecentEmojis(response.data.recentEmojis || []);
        }
      } catch (error) {
        console.error('Failed to fetch recent emojis:', error);
      }
    };
    fetchRecentEmojis();
  }, []);

  // Update current formats when selection changes
  useEffect(() => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    
    const handleSelectionChange = () => {
      const range = quill.getSelection();
      if (range) {
        const formats = quill.getFormat(range);
        
        // Check for font-weight in the DOM directly if not in formats
        if (!formats.weight && range.length > 0) {
          try {
            // Get the leaf at the current position
            const [leaf] = quill.getLeaf(range.index);
            if (leaf && leaf.domNode) {
              // Check for inline style font-weight
              const fontWeight = leaf.domNode.style?.fontWeight || 
                              leaf.domNode.parentElement?.style?.fontWeight ||
                              window.getComputedStyle(leaf.domNode).fontWeight;
              
              if (fontWeight && fontWeight !== 'normal' && fontWeight !== '400') {
                formats.weight = fontWeight;
              }
            }
          } catch (e) {
            // Silent fail - weight detection is optional
          }
        }
        
        // Check if bold is set and map it to weight 700
        if (formats.bold && !formats.weight) {
          formats.weight = '700';
        }
        
        // Normalize weight value
        if (formats.weight) {
          // Convert 'bold' to '700', 'normal' to '400', etc.
          if (formats.weight === 'bold') formats.weight = '700';
          else if (formats.weight === 'normal') formats.weight = '400';
          else if (!isNaN(formats.weight)) formats.weight = String(formats.weight);
        }
        
        // Default to 400 if no weight is detected
        if (!formats.weight) {
          formats.weight = '400';
        }
        
        setCurrentFormats(formats);
        setLastSelection(range);
      }
    };
    
    // Handle button edit events
    const handleButtonEdit = (e) => {
      const { node, value } = e.detail;
      setEditingButton(value);
      setEditingButtonNode(node);
      setButtonDialogOpen(true);
    };
    
    // Handle input edit events
    const handleInputEdit = (e) => {
      const { node, value } = e.detail;
      setEditingInput(value);
      setEditingInputNode(node);
      setInputDialogOpen(true);
    };
    
    quill.on('selection-change', handleSelectionChange);
    quill.on('text-change', handleSelectionChange);
    
    // Listen for button and input edit events
    const editor = quill.root;
    editor.addEventListener('button-edit', handleButtonEdit);
    editor.addEventListener('input-edit', handleInputEdit);
    
    return () => {
      quill.off('selection-change', handleSelectionChange);
      quill.off('text-change', handleSelectionChange);
      editor.removeEventListener('button-edit', handleButtonEdit);
      editor.removeEventListener('input-edit', handleInputEdit);
    };
  }, [quillRef]);
  const applyFormat = (format, value = null) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    
    if (value !== null) {
      quill.format(format, value);
    } else {
      const currentValue = quill.getFormat()[format];
      quill.format(format, !currentValue);
    }
  };

  const handleHeaderChange = (value) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    quill.format('header', value === '0' ? false : parseInt(value));
  };

  const handleFontChange = (value) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    quill.format('font', value === 'default' ? false : value);
  };

  const handleSizeChange = (value) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    quill.format('size', value === 'default' ? false : value);
  };

  const handleWeightChange = (value) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    
    // Get current selection
    const range = quill.getSelection();
    if (!range) {
      return;
    }
    
    // Apply the weight format
    quill.format('weight', value);
    
    // Also handle bold for compatibility
    if (value >= '700') {
      quill.format('bold', true);
    } else {
      quill.format('bold', false);
    }
    
    // Update current formats
    setTimeout(() => {
      const formats = quill.getFormat(range);
      setCurrentFormats({ ...formats, weight: value });
    }, 10);
  };

  const handleAlignChange = (align) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    quill.format('align', align === 'left' ? false : align);
  };

  const insertLink = () => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const value = prompt('Enter link URL:');
    if (value) {
      quill.format('link', value);
    }
  };

  const insertImage = () => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const value = prompt('Enter image URL:');
    if (value) {
      const range = quill.getSelection(true); // true = focus if needed
      if (range) {
        quill.insertEmbed(range.index, 'image', value);
        // Move cursor after the image
        quill.setSelection(range.index + 1);
      } else {
        // If no selection, insert at the end
        const length = quill.getLength();
        quill.insertEmbed(length - 1, 'image', value);
        quill.setSelection(length);
      }
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true); // true = focus if needed
        if (range) {
          quill.insertEmbed(range.index, 'image', base64Image);
          // Move cursor after the image
          quill.setSelection(range.index + 1);
        } else {
          // If no selection, insert at the end
          const length = quill.getLength();
          quill.insertEmbed(length - 1, 'image', base64Image);
          quill.setSelection(length);
        }
      }
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    event.target.value = '';
    setImageMenuAnchor(null);
  };

  // Update recent emojis on server
  const updateRecentEmojis = async (emoji) => {
    try {
      const response = await addRecentEmoji(emoji);
      if (response && response.data) {
        setRecentEmojis(response.data.recentEmojis || []);
      }
    } catch (error) {
      console.error('Failed to update recent emojis:', error);
      // Fallback to local storage
      const newRecent = [emoji, ...recentEmojis.filter(e => e !== emoji)].slice(0, 30);
      setRecentEmojis(newRecent);
    }
  };

  const insertPageBreak = () => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    
    if (range) {
      // Delete any selected content first
      if (range.length > 0) {
        quill.deleteText(range.index, range.length);
      }
      
      // Insert the page break using the custom blot
      quill.insertEmbed(range.index, 'pagebreak', true);
      
      // Insert a new line after the page break
      quill.insertText(range.index + 1, '\n');
      
      // Move cursor after the page break
      quill.setSelection(range.index + 2);
    }
  };

  const insertEmoji = (emoji) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    
    // Use stored selection or current selection
    let range = quill.getSelection() || lastSelection;
    
    // If still no range, create one at the end
    if (!range) {
      const length = quill.getLength();
      range = { index: length - 1, length: 0 };
    }
    
    // Focus the editor first
    quill.focus();
    
    // Insert the emoji
    quill.insertText(range.index, emoji);
    quill.setSelection(range.index + emoji.length);
    
    // Update recent emojis globally on server
    updateRecentEmojis(emoji);
    
    setEmojiMenuAnchor(null);
    setEmojiSearch('');
  };

  // Emoji categories
  const emojiCategories = {
    recent: {
      name: 'Recent',
      emojis: recentEmojis
    },
    smileys: {
      name: 'Smileys & Emotion',
      emojis: [
        '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
        '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛',
        '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
        '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😔', '😟',
        '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭',
        '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨',
        '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐',
        '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴',
        '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒',
        '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻',
        '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻',
        '😼', '😽', '🙀', '😿', '😾'
      ]
    },
    people: {
      name: 'People & Body',
      emojis: [
        '👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
        '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍',
        '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝',
        '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦵', '🦿', '🦶', '👂',
        '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁', '👅',
        '👄', '💋', '🩸', '👶', '👧', '🧒', '👦', '👩', '🧑', '👨',
        '👩‍🦱', '🧑‍🦱', '👨‍🦱', '👩‍🦰', '🧑‍🦰', '👨‍🦰', '👱‍♀️', '👱', '👱‍♂️', '👩‍🦳',
        '🧑‍🦳', '👨‍🦳', '👩‍🦲', '🧑‍🦲', '👨‍🦲', '🧔‍♀️', '🧔', '🧔‍♂️', '👵', '🧓',
        '👴', '👲', '👳‍♀️', '👳', '👳‍♂️', '🧕', '👮‍♀️', '👮', '👮‍♂️', '👷‍♀️',
        '👷', '👷‍♂️', '💂‍♀️', '💂', '💂‍♂️', '🕵️‍♀️', '🕵️', '🕵️‍♂️', '👩‍⚕️', '🧑‍⚕️',
        '👨‍⚕️', '👩‍🌾', '🧑‍🌾', '👨‍🌾', '👩‍🍳', '🧑‍🍳', '👨‍🍳', '👩‍🎓', '🧑‍🎓', '👨‍🎓'
      ]
    },
    nature: {
      name: 'Animals & Nature',
      emojis: [
        '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨',
        '🐯', '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊',
        '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉',
        '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🪱', '🐛', '🦋', '🐌',
        '🐞', '🐜', '🪰', '🪲', '🪳', '🦟', '🦗', '🕷', '🕸', '🦂',
        '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀',
        '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆',
        '🦓', '🦍', '🦧', '🦣', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒',
        '🦘', '🦬', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙',
        '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🪶', '🐓',
        '🦃', '🦤', '🦚', '🦜', '🦢', '🦩', '🕊', '🐇', '🦝', '🦨',
        '🦡', '🦫', '🦦', '🦥', '🐁', '🐀', '🐿', '🦔', '🐾', '🐉',
        '🐲', '🌵', '🎄', '🌲', '🌳', '🌴', '🪵', '🌱', '🌿', '☘️',
        '🍀', '🎍', '🪴', '🎋', '🍃', '🍂', '🍁', '🍄', '🐚', '🪨',
        '🌾', '💐', '🌷', '🌹', '🥀', '🌺', '🌸', '🌼', '🌻', '🌞',
        '🌝', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒',
        '🌓', '🌔', '🌙', '🌎', '🌍', '🌏', '🪐', '💫', '⭐', '🌟',
        '✨', '⚡', '☄️', '💥', '🔥', '🌪', '🌈', '☀️', '🌤', '⛅',
        '🌥', '☁️', '🌦', '🌧', '⛈', '🌩', '🌨', '❄️', '☃️', '⛄',
        '🌬', '💨', '💧', '💦', '☔', '☂️', '🌊', '🌫'
      ]
    },
    food: {
      name: 'Food & Drink',
      emojis: [
        '🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐',
        '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑',
        '🥦', '🥬', '🥒', '🌶', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅',
        '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳',
        '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔',
        '🍟', '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗',
        '🥘', '🫕', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟',
        '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡',
        '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬',
        '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '🫖',
        '☕', '🍵', '🧃', '🥤', '🧋', '🍶', '🍺', '🍻', '🥂', '🍷',
        '🥃', '🍸', '🍹', '🧉', '🍾', '🧊', '🥄', '🍴', '🍽', '🥣',
        '🥡', '🥢', '🧂'
      ]
    },
    activities: {
      name: 'Activities',
      emojis: [
        '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
        '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳',
        '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷',
        '⛸', '🥌', '🎿', '⛷', '🏂', '🪂', '🏋️‍♀️', '🏋️', '🏋️‍♂️', '🤼‍♀️',
        '🤼', '🤼‍♂️', '🤸‍♀️', '🤸', '🤸‍♂️', '⛹️‍♀️', '⛹️', '⛹️‍♂️', '🤺', '🤾‍♀️',
        '🤾', '🤾‍♂️', '🏌️‍♀️', '🏌️', '🏌️‍♂️', '🏇', '🧘‍♀️', '🧘', '🧘‍♂️', '🏄‍♀️',
        '🏄', '🏄‍♂️', '🏊‍♀️', '🏊', '🏊‍♂️', '🤽‍♀️', '🤽', '🤽‍♂️', '🚣‍♀️', '🚣',
        '🚣‍♂️', '🧗‍♀️', '🧗', '🧗‍♂️', '🚵‍♀️', '🚵', '🚵‍♂️', '🚴‍♀️', '🚴', '🚴‍♂️',
        '🏆', '🥇', '🥈', '🥉', '🏅', '🎖', '🏵', '🎗', '🎫', '🎟',
        '🎪', '🤹‍♀️', '🤹', '🤹‍♂️', '🎭', '🩰', '🎨', '🎬', '🎤', '🎧',
        '🎼', '🎹', '🥁', '🪘', '🎷', '🎺', '🪗', '🎸', '🪕', '🎻',
        '🎲', '♟', '🎯', '🎳', '🎮', '🎰', '🧩'
      ]
    },
    travel: {
      name: 'Travel & Places',
      emojis: [
        '🚗', '🚕', '🚙', '🚌', '🚎', '🏎', '🚓', '🚑', '🚒', '🚐',
        '🛻', '🚚', '🚛', '🚜', '🦯', '🦽', '🦼', '🛴', '🚲', '🛵',
        '🏍', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟',
        '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇',
        '🚊', '🚉', '✈️', '🛫', '🛬', '🛩', '💺', '🛰', '🚀', '🛸',
        '🚁', '🛶', '⛵', '🚤', '🛥', '🛳', '⛴', '🚢', '⚓', '🪝',
        '⛽', '🚧', '🚦', '🚥', '🚏', '🗺', '🗿', '🗽', '🗼', '🏰',
        '🏯', '🏟', '🎡', '🎢', '🎠', '⛲', '⛱', '🏖', '🏝', '🏜',
        '🌋', '⛰', '🏔', '🗻', '🏕', '⛺', '🛖', '🏠', '🏡', '🏘',
        '🏚', '🏗', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏨',
        '🏩', '🏪', '🏫', '🏛', '⛪', '🕌', '🕍', '🛕', '🕋', '⛩',
        '🛤', '🛣', '🗾', '🎑', '🏞', '🌅', '🌄', '🌠', '🎇', '🎆',
        '🌇', '🌆', '🏙', '🌃', '🌌', '🌉', '🌁'
      ]
    },
    objects: {
      name: 'Objects',
      emojis: [
        '⌚', '📱', '📲', '💻', '⌨️', '🖥', '🖨', '🖱', '🖲', '🕹',
        '🗜', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥',
        '📽', '🎞', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚',
        '🎛', '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋',
        '🔌', '💡', '🔦', '🕯', '🪔', '🧯', '🛢', '💸', '💵', '💴',
        '💶', '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🪛',
        '🔧', '🔨', '⚒', '🛠', '⛏', '🪚', '🔩', '⚙️', '🪤', '🧱',
        '⛓', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡', '⚔️', '🛡',
        '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️',
        '🔭', '🔬', '🕳', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠',
        '🧫', '🧪', '🌡', '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿',
        '🛁', '🛀', '🧼', '🪥', '🪒', '🧽', '🪣', '🧴', '🛎', '🔑',
        '🗝', '🚪', '🪑', '🛋', '🛏', '🛌', '🧸', '🪆', '🖼', '🪞',
        '🪟', '🛍', '🛒', '🎁', '🎈', '🎏', '🎀', '🪄', '🪅', '🎊',
        '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌',
        '📥', '📤', '📦', '🏷', '🪧', '📪', '📫', '📬', '📭', '📮',
        '📯', '📜', '📃', '📄', '📑', '🧾', '📊', '📈', '📉', '🗒',
        '🗓', '📆', '📅', '🗑', '📇', '🗃', '🗳', '🗄', '📋', '📁',
        '📂', '🗂', '🗞', '📰', '📓', '📔', '📒', '📕', '📗', '📘',
        '📙', '📚', '📖', '🔖', '🧷', '🔗', '📎', '🖇', '📐', '📏',
        '🧮', '📌', '📍', '✂️', '🖊', '🖋', '✒️', '🖌', '🖍', '📝',
        '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓'
      ]
    },
    symbols: {
      name: 'Symbols',
      emojis: [
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
        '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
        '✝️', '☪️', '🕉', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
        '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
        '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
        '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️',
        '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️',
        '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️',
        '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓',
        '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️',
        '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠',
        'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🛗', '🈳', '🈂️',
        '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚧', '🚻', '🚮',
        '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗',
        '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣',
        '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '⏏️', '▶️',
        '⏸', '⏯', '⏹', '⏺', '⏭', '⏮', '⏩', '⏪', '⏫', '⏬',
        '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️',
        '↖️', '↕️', '↔️', '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂',
        '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '♾', '💲',
        '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚', '🔙', '🔛',
        '🔝', '🔜', '✔️', '☑️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵',
        '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷',
        '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧',
        '🟨', '🟩', '🟦', '🟪', '⬛', '⬜', '🟫', '🔈', '🔇', '🔉',
        '🔊', '🔔', '🔕', '📣', '📢', '💬', '💭', '🗯', '♠️', '♣️',
        '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔',
        '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞',
        '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧'
      ]
    },
    flags: {
      name: 'Flags',
      emojis: [
        '🏳️', '🏴', '🏴‍☠️', '🏁', '🚩', '🏳️‍🌈', '🏳️‍⚧️', '🇺🇳', '🇦🇫', '🇦🇽',
        '🇦🇱', '🇩🇿', '🇦🇸', '🇦🇩', '🇦🇴', '🇦🇮', '🇦🇶', '🇦🇬', '🇦🇷', '🇦🇲',
        '🇦🇼', '🇦🇺', '🇦🇹', '🇦🇿', '🇧🇸', '🇧🇭', '🇧🇩', '🇧🇧', '🇧🇾', '🇧🇪',
        '🇧🇿', '🇧🇯', '🇧🇲', '🇧🇹', '🇧🇴', '🇧🇦', '🇧🇼', '🇧🇷', '🇮🇴', '🇻🇬',
        '🇧🇳', '🇧🇬', '🇧🇫', '🇧🇮', '🇰🇭', '🇨🇲', '🇨🇦', '🇮🇨', '🇨🇻', '🇧🇶',
        '🇰🇾', '🇨🇫', '🇹🇩', '🇨🇱', '🇨🇳', '🇨🇽', '🇨🇨', '🇨🇴', '🇰🇲', '🇨🇬',
        '🇨🇩', '🇨🇰', '🇨🇷', '🇨🇮', '🇭🇷', '🇨🇺', '🇨🇼', '🇨🇾', '🇨🇿', '🇩🇰',
        '🇩🇯', '🇩🇲', '🇩🇴', '🇪🇨', '🇪🇬', '🇸🇻', '🇬🇶', '🇪🇷', '🇪🇪', '🇸🇿',
        '🇪🇹', '🇪🇺', '🇫🇰', '🇫🇴', '🇫🇯', '🇫🇮', '🇫🇷', '🇬🇫', '🇵🇫', '🇹🇫',
        '🇬🇦', '🇬🇲', '🇬🇪', '🇩🇪', '🇬🇭', '🇬🇮', '🇬🇷', '🇬🇱', '🇬🇩', '🇬🇵',
        '🇬🇺', '🇬🇹', '🇬🇬', '🇬🇳', '🇬🇼', '🇬🇾', '🇭🇹', '🇭🇳', '🇭🇰', '🇭🇺',
        '🇮🇸', '🇮🇳', '🇮🇩', '🇮🇷', '🇮🇶', '🇮🇪', '🇮🇲', '🇮🇱', '🇮🇹', '🇯🇲',
        '🇯🇵', '🎌', '🇯🇪', '🇯🇴', '🇰🇿', '🇰🇪', '🇰🇮', '🇽🇰', '🇰🇼', '🇰🇬',
        '🇱🇦', '🇱🇻', '🇱🇧', '🇱🇸', '🇱🇷', '🇱🇾', '🇱🇮', '🇱🇹', '🇱🇺', '🇲🇴',
        '🇲🇬', '🇲🇼', '🇲🇾', '🇲🇻', '🇲🇱', '🇲🇹', '🇲🇭', '🇲🇶', '🇲🇷', '🇲🇺',
        '🇾🇹', '🇲🇽', '🇫🇲', '🇲🇩', '🇲🇨', '🇲🇳', '🇲🇪', '🇲🇸', '🇲🇦', '🇲🇿',
        '🇲🇲', '🇳🇦', '🇳🇷', '🇳🇵', '🇳🇱', '🇳🇨', '🇳🇿', '🇳🇮', '🇳🇪', '🇳🇬',
        '🇳🇺', '🇳🇫', '🇰🇵', '🇲🇰', '🇲🇵', '🇳🇴', '🇴🇲', '🇵🇰', '🇵🇼', '🇵🇸',
        '🇵🇦', '🇵🇬', '🇵🇾', '🇵🇪', '🇵🇭', '🇵🇳', '🇵🇱', '🇵🇹', '🇵🇷', '🇶🇦',
        '🇷🇪', '🇷🇴', '🇷🇺', '🇷🇼', '🇼🇸', '🇸🇲', '🇸🇹', '🇸🇦', '🇸🇳', '🇷🇸',
        '🇸🇨', '🇸🇱', '🇸🇬', '🇸🇽', '🇸🇰', '🇸🇮', '🇬🇸', '🇸🇧', '🇸🇴', '🇿🇦',
        '🇰🇷', '🇸🇸', '🇪🇸', '🇱🇰', '🇧🇱', '🇸🇭', '🇰🇳', '🇱🇨', '🇵🇲', '🇻🇨',
        '🇸🇩', '🇸🇷', '🇸🇪', '🇨🇭', '🇸🇾', '🇹🇼', '🇹🇯', '🇹🇿', '🇹🇭', '🇹🇱',
        '🇹🇬', '🇹🇰', '🇹🇴', '🇹🇹', '🇹🇳', '🇹🇷', '🇹🇲', '🇹🇨', '🇹🇻', '🇻🇮',
        '🇺🇬', '🇺🇦', '🇦🇪', '🇬🇧', '🏴󠁧󠁢󠁥󠁮󠁧󠁿', '🏴󠁧󠁢󠁳󠁣󠁴󠁿', '🏴󠁧󠁢󠁷󠁬󠁳󠁿', '🇺🇸', '🇺🇾', '🇺🇿',
        '🇻🇺', '🇻🇦', '🇻🇪', '🇻🇳', '🇼🇫', '🇪🇭', '🇾🇪', '🇿🇲', '🇿🇼'
      ]
    }
  };

  // Filter emojis based on search
  const getFilteredEmojis = (category) => {
    if (!emojiSearch || emojiSearch.trim() === '') return category.emojis;
    
    const searchTerm = emojiSearch.toLowerCase().trim();
    
    // If searching, combine all categories except recent
    if (category.name === 'Recent' && searchTerm) {
      // When searching, show results from all categories
      const allEmojis = Object.values(emojiCategories)
        .filter(cat => cat.name !== 'Recent')
        .flatMap(cat => cat.emojis);
      
      // Remove duplicates
      const uniqueEmojis = [...new Set(allEmojis)];
      
      // Search by emoji character or by emoji names
      return uniqueEmojis.filter(emoji => {
        // Direct emoji match
        if (emoji.toLowerCase().includes(searchTerm)) return true;
        
        // Check against emoji names/keywords
        const emojiName = getEmojiName(emoji).toLowerCase();
        return emojiName.includes(searchTerm);
      }).slice(0, 50); // Limit search results
    }
    
    // For other categories, just filter within that category
    return category.emojis.filter(emoji => {
      if (emoji.toLowerCase().includes(searchTerm)) return true;
      const emojiName = getEmojiName(emoji).toLowerCase();
      return emojiName.includes(searchTerm);
    });
  };

  // Helper function to get emoji names/keywords for search
  const getEmojiName = (emoji) => {
    // Basic emoji to name mapping for common emojis
    const emojiNames = {
      '😀': 'grinning face happy smile',
      '😃': 'smiley happy face big eyes',
      '😄': 'smile happy grin',
      '😁': 'beaming face happy',
      '😆': 'laughing satisfied happy',
      '😅': 'sweat smile relief',
      '😂': 'joy tears laughing',
      '🤣': 'rofl rolling floor laughing',
      '😊': 'blush happy smile',
      '😇': 'innocent angel halo',
      '🙂': 'slightly smiling face',
      '😉': 'wink winking face',
      '😌': 'relieved face',
      '😍': 'heart eyes love',
      '🥰': 'smiling hearts love',
      '😘': 'kiss kissing face',
      '😗': 'kissing face',
      '😙': 'kissing smiling eyes',
      '😚': 'kissing closed eyes',
      '😋': 'yum delicious tongue',
      '😛': 'tongue face',
      '😝': 'tongue winking',
      '😜': 'winking tongue',
      '🤪': 'zany face crazy',
      '🤨': 'raised eyebrow skeptical',
      '🧐': 'monocle face',
      '🤓': 'nerd face glasses',
      '😎': 'sunglasses cool',
      '🤩': 'star eyes excited',
      '😏': 'smirk smirking',
      '😒': 'unamused face',
      '😞': 'disappointed sad',
      '😔': 'pensive sad',
      '😟': 'worried face',
      '😕': 'confused face',
      '😣': 'persevering face',
      '😖': 'confounded face',
      '😫': 'tired face',
      '😩': 'weary face',
      '🥺': 'pleading face puppy eyes',
      '😢': 'cry sad tear',
      '😭': 'loudly crying sob',
      '😤': 'triumph steam nose',
      '😠': 'angry mad face',
      '😡': 'pouting angry red',
      '🤬': 'cursing swearing angry',
      '🤯': 'exploding head mind blown',
      '😳': 'flushed blushing',
      '🥵': 'hot face sweating',
      '🥶': 'cold face freezing',
      '😱': 'scream scared shocked',
      '😨': 'fearful scared',
      '😰': 'anxious sweat worried',
      '😥': 'sad relieved disappointed',
      '😓': 'downcast sweat',
      '🤗': 'hugging face hug',
      '🤔': 'thinking face hmm',
      '🤭': 'hand over mouth giggling',
      '🤫': 'shushing face quiet',
      '🤥': 'lying face pinocchio',
      '😶': 'no mouth speechless',
      '😐': 'neutral face meh',
      '😑': 'expressionless face',
      '😬': 'grimacing face awkward',
      '🙄': 'eye roll eyeroll',
      '😯': 'hushed surprised wow',
      '😦': 'frowning open mouth',
      '😧': 'anguished face',
      '😮': 'open mouth surprised',
      '😲': 'astonished shocked',
      '😴': 'sleeping face sleep',
      '🤤': 'drooling face',
      '😪': 'sleepy face tired',
      '😵': 'dizzy face',
      '🤐': 'zipper mouth quiet',
      '🥴': 'woozy face drunk',
      '🤢': 'nauseated sick green',
      '🤮': 'vomiting face sick',
      '🤧': 'sneezing face achoo',
      '😷': 'medical mask sick',
      '🤒': 'thermometer sick fever',
      '🤕': 'bandage hurt injured',
      '🤑': 'money mouth dollar',
      '🤠': 'cowboy hat face',
      '👍': 'thumbs up like good',
      '👎': 'thumbs down dislike bad',
      '👌': 'ok hand okay',
      '✌️': 'victory peace sign',
      '🤞': 'crossed fingers luck',
      '🤟': 'love you hand sign',
      '🤘': 'rock on horns',
      '🤙': 'call me hand phone',
      '👈': 'left pointing finger',
      '👉': 'right pointing finger',
      '👆': 'up pointing finger',
      '👇': 'down pointing finger',
      '☝️': 'index pointing up',
      '✋': 'raised hand stop',
      '🤚': 'raised back hand',
      '🖐️': 'hand splayed five',
      '🖖': 'vulcan salute spock',
      '👋': 'waving hand hello bye',
      '🤏': 'pinching hand small',
      '✍️': 'writing hand',
      '👏': 'clapping hands applause',
      '🙌': 'raising hands celebration',
      '👐': 'open hands hug',
      '🤲': 'palms up together',
      '🤝': 'handshake deal',
      '🙏': 'folded hands pray thanks',
      '❤️': 'red heart love',
      '🧡': 'orange heart',
      '💛': 'yellow heart',
      '💚': 'green heart',
      '💙': 'blue heart',
      '💜': 'purple heart',
      '🖤': 'black heart',
      '🤍': 'white heart',
      '🤎': 'brown heart',
      '💔': 'broken heart',
      '❣️': 'heart exclamation',
      '💕': 'two hearts love',
      '💞': 'revolving hearts',
      '💓': 'beating heart',
      '💗': 'growing heart',
      '💖': 'sparkling heart',
      '💘': 'heart arrow cupid',
      '💝': 'heart ribbon gift',
      '🐶': 'dog face puppy',
      '🐱': 'cat face kitty',
      '🐭': 'mouse face',
      '🐹': 'hamster face',
      '🐰': 'rabbit face bunny',
      '🦊': 'fox face',
      '🐻': 'bear face',
      '🐼': 'panda face',
      '🐨': 'koala face',
      '🐯': 'tiger face',
      '🦁': 'lion face',
      '🐮': 'cow face',
      '🐷': 'pig face',
      '🐸': 'frog face',
      '🐵': 'monkey face',
      '🙈': 'see no evil monkey',
      '🙉': 'hear no evil monkey',
      '🙊': 'speak no evil monkey',
      '🐔': 'chicken',
      '🐧': 'penguin',
      '🐦': 'bird',
      '🐤': 'baby chick',
      '🦆': 'duck',
      '🦅': 'eagle',
      '🦉': 'owl',
      '🦇': 'bat',
      '🐺': 'wolf',
      '🐗': 'boar',
      '🐴': 'horse face',
      '🦄': 'unicorn',
      '🐝': 'bee honeybee',
      '🐛': 'bug caterpillar',
      '🦋': 'butterfly',
      '🐌': 'snail',
      '🐞': 'lady beetle bug',
      '🐜': 'ant',
      '🦟': 'mosquito',
      '🦗': 'cricket',
      '🕷️': 'spider',
      '🦂': 'scorpion',
      '🐢': 'turtle',
      '🐍': 'snake',
      '🦎': 'lizard',
      '🦖': 'trex dinosaur',
      '🦕': 'sauropod dinosaur',
      '🐙': 'octopus',
      '🦑': 'squid',
      '🦐': 'shrimp',
      '🦞': 'lobster',
      '🦀': 'crab',
      '🐡': 'blowfish',
      '🐠': 'tropical fish',
      '🐟': 'fish',
      '🐬': 'dolphin',
      '🐳': 'whale spouting',
      '🐋': 'whale',
      '🦈': 'shark',
      '🐊': 'crocodile',
      '🐅': 'tiger',
      '🐆': 'leopard',
      '🦓': 'zebra',
      '🦍': 'gorilla',
      '🦧': 'orangutan',
      '🐘': 'elephant',
      '🦛': 'hippopotamus',
      '🦏': 'rhinoceros',
      '🐪': 'camel dromedary',
      '🐫': 'two hump camel',
      '🦒': 'giraffe',
      '🦘': 'kangaroo',
      '🐃': 'water buffalo',
      '🐂': 'ox',
      '🐄': 'cow',
      '🐎': 'horse',
      '🐖': 'pig',
      '🐏': 'ram',
      '🐑': 'ewe sheep',
      '🦙': 'llama',
      '🐐': 'goat',
      '🦌': 'deer',
      '🐕': 'dog',
      '🐩': 'poodle',
      '🦮': 'guide dog',
      '🐕‍🦺': 'service dog',
      '🐈': 'cat',
      '🐓': 'rooster',
      '🦃': 'turkey',
      '🦚': 'peacock',
      '🦜': 'parrot',
      '🦢': 'swan',
      '🦩': 'flamingo',
      '🕊️': 'dove peace',
      '🐇': 'rabbit',
      '🦝': 'raccoon',
      '🦨': 'skunk',
      '🦡': 'badger',
      '🦫': 'beaver',
      '🦦': 'otter',
      '🦥': 'sloth',
      '🐁': 'mouse',
      '🐀': 'rat',
      '🐿️': 'chipmunk squirrel',
      '🦔': 'hedgehog',
      '🌵': 'cactus',
      '🎄': 'christmas tree',
      '🌲': 'evergreen tree',
      '🌳': 'deciduous tree',
      '🌴': 'palm tree',
      '🌱': 'seedling plant',
      '🌿': 'herb',
      '☘️': 'shamrock',
      '🍀': 'four leaf clover luck',
      '🎍': 'bamboo',
      '🎋': 'tanabata tree',
      '🍃': 'leaves wind',
      '🍂': 'fallen leaf autumn',
      '🍁': 'maple leaf',
      '🌾': 'ear of rice',
      '💐': 'bouquet flowers',
      '🌷': 'tulip',
      '🌹': 'rose flower',
      '🥀': 'wilted flower',
      '🌺': 'hibiscus',
      '🌸': 'cherry blossom',
      '🌼': 'blossom flower',
      '🌻': 'sunflower',
      '🌞': 'sun face',
      '🌝': 'full moon face',
      '🌛': 'first quarter moon face',
      '🌜': 'last quarter moon face',
      '🌚': 'new moon face',
      '🌕': 'full moon',
      '🌖': 'waning gibbous moon',
      '🌗': 'last quarter moon',
      '🌘': 'waning crescent moon',
      '🌑': 'new moon',
      '🌒': 'waxing crescent moon',
      '🌓': 'first quarter moon',
      '🌔': 'waxing gibbous moon',
      '🌙': 'crescent moon',
      '🌎': 'earth americas',
      '🌍': 'earth africa',
      '🌏': 'earth asia',
      '🪐': 'ringed planet saturn',
      '💫': 'dizzy star',
      '⭐': 'star',
      '🌟': 'glowing star',
      '✨': 'sparkles',
      '⚡': 'lightning bolt',
      '☄️': 'comet',
      '💥': 'collision boom',
      '🔥': 'fire flame hot',
      '🌪️': 'tornado',
      '🌈': 'rainbow',
      '☀️': 'sun sunny',
      '🌤️': 'sun small cloud',
      '⛅': 'sun behind cloud',
      '🌥️': 'sun large cloud',
      '☁️': 'cloud cloudy',
      '🌦️': 'sun rain cloud',
      '🌧️': 'rain cloud',
      '⛈️': 'thunder cloud storm',
      '🌩️': 'lightning cloud',
      '🌨️': 'snow cloud',
      '❄️': 'snowflake',
      '☃️': 'snowman',
      '⛄': 'snowman without snow',
      '🌬️': 'wind face blowing',
      '💨': 'dash wind',
      '💧': 'droplet water',
      '💦': 'sweat droplets',
      '☔': 'umbrella rain',
      '☂️': 'umbrella',
      '🌊': 'wave water ocean',
      '🌫️': 'fog',
      '🍏': 'green apple',
      '🍎': 'red apple',
      '🍐': 'pear',
      '🍊': 'orange tangerine',
      '🍋': 'lemon',
      '🍌': 'banana',
      '🍉': 'watermelon',
      '🍇': 'grapes',
      '🍓': 'strawberry',
      '🫐': 'blueberries',
      '🍈': 'melon',
      '🍒': 'cherries',
      '🍑': 'peach',
      '🥭': 'mango',
      '🍍': 'pineapple',
      '🥥': 'coconut',
      '🥝': 'kiwi fruit',
      '🍅': 'tomato',
      '🍆': 'eggplant',
      '🥑': 'avocado',
      '🥦': 'broccoli',
      '🥬': 'leafy green',
      '🥒': 'cucumber',
      '🌶️': 'hot pepper',
      '🫑': 'bell pepper',
      '🌽': 'corn',
      '🥕': 'carrot',
      '🫒': 'olive',
      '🧄': 'garlic',
      '🧅': 'onion',
      '🥔': 'potato',
      '🍠': 'sweet potato',
      '🥐': 'croissant',
      '🥖': 'baguette bread',
      '🥨': 'pretzel',
      '🧀': 'cheese',
      '🥚': 'egg',
      '🍳': 'cooking fried egg',
      '🧈': 'butter',
      '🥞': 'pancakes',
      '🧇': 'waffle',
      '🥓': 'bacon',
      '🥩': 'steak meat',
      '🍗': 'poultry leg chicken',
      '🍖': 'meat on bone',
      '🌭': 'hot dog',
      '🍔': 'hamburger burger',
      '🍟': 'french fries',
      '🍕': 'pizza',
      '🥪': 'sandwich',
      '🥙': 'stuffed flatbread',
      '🧆': 'falafel',
      '🌮': 'taco',
      '🌯': 'burrito',
      '🫔': 'tamale',
      '🥗': 'salad',
      '🥘': 'shallow pan food paella',
      '🫕': 'fondue',
      '🥫': 'canned food',
      '🍝': 'spaghetti pasta',
      '🍜': 'steaming bowl noodles',
      '🍲': 'pot of food stew',
      '🍛': 'curry rice',
      '🍣': 'sushi',
      '🍱': 'bento box',
      '🥟': 'dumpling',
      '🦪': 'oyster',
      '🍤': 'fried shrimp',
      '🍙': 'rice ball',
      '🍚': 'cooked rice',
      '🍘': 'rice cracker',
      '🍥': 'fish cake',
      '🥠': 'fortune cookie',
      '🥮': 'moon cake',
      '🍢': 'oden',
      '🍡': 'dango',
      '🍧': 'shaved ice',
      '🍨': 'ice cream',
      '🍦': 'soft ice cream',
      '🥧': 'pie',
      '🧁': 'cupcake',
      '🍰': 'cake slice',
      '🎂': 'birthday cake',
      '🍮': 'custard',
      '🍭': 'lollipop',
      '🍬': 'candy',
      '🍫': 'chocolate bar',
      '🍿': 'popcorn',
      '🍩': 'doughnut',
      '🍪': 'cookie',
      '🌰': 'chestnut',
      '🥜': 'peanuts',
      '🍯': 'honey pot',
      '🥛': 'glass milk',
      '🍼': 'baby bottle',
      '☕': 'coffee hot',
      '🫖': 'teapot',
      '🍵': 'tea cup',
      '🍶': 'sake',
      '🍾': 'champagne bottle',
      '🍷': 'wine glass',
      '🍸': 'cocktail glass',
      '🍹': 'tropical drink',
      '🍺': 'beer mug',
      '🍻': 'beers cheers',
      '🥂': 'clinking glasses',
      '🥃': 'tumbler glass whiskey',
      '🥤': 'cup straw',
      '🧋': 'bubble tea',
      '🧃': 'juice box',
      '🧉': 'mate',
      '🧊': 'ice cube',
      '🥢': 'chopsticks',
      '🍽️': 'fork knife plate',
      '🍴': 'fork knife',
      '🥄': 'spoon',
      '🔪': 'kitchen knife',
      '🏺': 'amphora',
      '🎃': 'jack lantern halloween',
      '🎆': 'fireworks',
      '🎇': 'sparkler',
      '🎈': 'balloon',
      '🎉': 'party popper',
      '🎊': 'confetti ball',
      '🎋': 'tanabata tree',
      '🎍': 'pine decoration',
      '🎎': 'dolls',
      '🎏': 'carp streamer',
      '🎐': 'wind chime',
      '🎑': 'moon ceremony',
      '🎀': 'ribbon bow',
      '🎁': 'gift present',
      '🎗️': 'reminder ribbon',
      '🎟️': 'admission ticket',
      '🎫': 'ticket',
      '🏅': 'medal sports',
      '🏆': 'trophy',
      '🥇': 'first place medal gold',
      '🥈': 'second place medal silver',
      '🥉': 'third place medal bronze',
      '⚽': 'soccer ball football',
      '⚾': 'baseball',
      '🥎': 'softball',
      '🏀': 'basketball',
      '🏐': 'volleyball',
      '🏈': 'american football',
      '🏉': 'rugby football',
      '🎾': 'tennis',
      '🥏': 'flying disc frisbee',
      '🎳': 'bowling',
      '🏏': 'cricket game',
      '🏑': 'field hockey',
      '🏒': 'ice hockey',
      '🥍': 'lacrosse',
      '🏓': 'ping pong',
      '🏸': 'badminton',
      '🥊': 'boxing glove',
      '🥋': 'martial arts uniform',
      '🥅': 'goal net',
      '⛳': 'flag in hole golf',
      '⛸️': 'ice skate',
      '🎣': 'fishing pole',
      '🤿': 'diving mask',
      '🎽': 'running shirt',
      '🎿': 'skis',
      '🛷': 'sled',
      '🥌': 'curling stone',
      '🎯': 'dart bullseye',
      '🪀': 'yoyo',
      '🪁': 'kite',
      '🎱': 'pool 8 ball',
      '🔮': 'crystal ball',
      '🪄': 'magic wand',
      '🎮': 'video game controller',
      '🕹️': 'joystick',
      '🎰': 'slot machine',
      '🎲': 'game die dice',
      '🧩': 'puzzle piece',
      '🧸': 'teddy bear',
      '🪅': 'pinata',
      '🪆': 'nesting dolls',
      '♠️': 'spade suit',
      '♥️': 'heart suit',
      '♦️': 'diamond suit',
      '♣️': 'club suit',
      '♟️': 'chess pawn',
      '🃏': 'joker',
      '🀄': 'mahjong red dragon',
      '🎴': 'flower playing cards',
      '🏠': 'house home',
      '🏡': 'house garden',
      '🏢': 'office building',
      '🏣': 'japanese post office',
      '🏤': 'post office',
      '🏥': 'hospital',
      '🏦': 'bank',
      '🏨': 'hotel',
      '🏩': 'love hotel',
      '🏪': 'convenience store',
      '🏫': 'school',
      '🏬': 'department store',
      '🏭': 'factory',
      '🏯': 'japanese castle',
      '🏰': 'castle',
      '💒': 'wedding chapel',
      '🗼': 'tokyo tower',
      '🗽': 'statue liberty',
      '⛪': 'church',
      '🕌': 'mosque',
      '🛕': 'hindu temple',
      '🕍': 'synagogue',
      '⛩️': 'shinto shrine',
      '🕋': 'kaaba',
      '⛲': 'fountain',
      '⛺': 'tent camping',
      '🌁': 'foggy',
      '🌃': 'night stars',
      '🏙️': 'cityscape',
      '🌄': 'sunrise mountains',
      '🌅': 'sunrise',
      '🌆': 'cityscape dusk',
      '🌇': 'sunset',
      '🌉': 'bridge night',
      '♨️': 'hot springs',
      '🎠': 'carousel horse',
      '🎡': 'ferris wheel',
      '🎢': 'roller coaster',
      '💈': 'barber pole',
      '🎪': 'circus tent',
      '🚂': 'locomotive train',
      '🚃': 'railway car',
      '🚄': 'high speed train',
      '🚅': 'bullet train',
      '🚆': 'train',
      '🚇': 'metro subway',
      '🚈': 'light rail',
      '🚉': 'station',
      '🚊': 'tram',
      '🚝': 'monorail',
      '🚞': 'mountain railway',
      '🚋': 'tram car',
      '🚌': 'bus',
      '🚍': 'oncoming bus',
      '🚎': 'trolleybus',
      '🚐': 'minibus',
      '🚑': 'ambulance',
      '🚒': 'fire engine',
      '🚓': 'police car',
      '🚔': 'oncoming police car',
      '🚕': 'taxi',
      '🚖': 'oncoming taxi',
      '🚗': 'car automobile',
      '🚘': 'oncoming automobile',
      '🚙': 'suv',
      '🛻': 'pickup truck',
      '🚚': 'delivery truck',
      '🚛': 'articulated lorry',
      '🚜': 'tractor',
      '🏎️': 'racing car',
      '🏍️': 'motorcycle',
      '🛵': 'motor scooter',
      '🦽': 'manual wheelchair',
      '🦼': 'motorized wheelchair',
      '🛺': 'auto rickshaw',
      '🚲': 'bicycle bike',
      '🛴': 'kick scooter',
      '🛹': 'skateboard',
      '🛼': 'roller skate',
      '🚏': 'bus stop',
      '🛣️': 'motorway',
      '🛤️': 'railway track',
      '🛢️': 'oil drum',
      '⛽': 'fuel pump gas',
      '🚨': 'police car light',
      '🚥': 'traffic light horizontal',
      '🚦': 'traffic light vertical',
      '🛑': 'stop sign',
      '🚧': 'construction',
      '⚓': 'anchor',
      '⛵': 'sailboat',
      '🛶': 'canoe',
      '🚤': 'speedboat',
      '🛳️': 'passenger ship',
      '⛴️': 'ferry',
      '🛥️': 'motor boat',
      '🚢': 'ship',
      '✈️': 'airplane',
      '🛩️': 'small airplane',
      '🛫': 'airplane departure',
      '🛬': 'airplane arrival',
      '🪂': 'parachute',
      '💺': 'seat',
      '🚁': 'helicopter',
      '🚟': 'suspension railway',
      '🚠': 'mountain cableway',
      '🚡': 'aerial tramway',
      '🛰️': 'satellite',
      '🚀': 'rocket',
      '🛸': 'flying saucer ufo',
      '🛎️': 'bellhop bell',
      '🧳': 'luggage',
      '⌛': 'hourglass',
      '⏳': 'hourglass flowing',
      '⌚': 'watch',
      '⏰': 'alarm clock',
      '⏱️': 'stopwatch',
      '⏲️': 'timer clock',
      '🕰️': 'mantelpiece clock',
      '🕛': 'twelve oclock',
      '🕧': 'twelve thirty',
      '🕐': 'one oclock',
      '🕜': 'one thirty',
      '🕑': 'two oclock',
      '🕝': 'two thirty',
      '🕒': 'three oclock',
      '🕞': 'three thirty',
      '🕓': 'four oclock',
      '🕟': 'four thirty',
      '🕔': 'five oclock',
      '🕠': 'five thirty',
      '🕕': 'six oclock',
      '🕡': 'six thirty',
      '🕖': 'seven oclock',
      '🕢': 'seven thirty',
      '🕗': 'eight oclock',
      '🕣': 'eight thirty',
      '🕘': 'nine oclock',
      '🕤': 'nine thirty',
      '🕙': 'ten oclock',
      '🕥': 'ten thirty',
      '🕚': 'eleven oclock',
      '🕦': 'eleven thirty',
      '🌑': 'new moon',
      '🌒': 'waxing crescent moon',
      '🌓': 'first quarter moon',
      '🌔': 'waxing gibbous moon',
      '🌕': 'full moon',
      '🌖': 'waning gibbous moon',
      '🌗': 'last quarter moon',
      '🌘': 'waning crescent moon',
      '🌙': 'crescent moon',
      '🌚': 'new moon face',
      '🌛': 'first quarter moon face',
      '🌜': 'last quarter moon face',
      '🌡️': 'thermometer',
      '☀️': 'sun',
      '🌝': 'full moon face',
      '🌞': 'sun with face',
      '🪐': 'ringed planet',
      '⭐': 'star',
      '🌟': 'glowing star',
      '🌠': 'shooting star',
      '🌌': 'milky way',
      '☁️': 'cloud',
      '⛅': 'sun behind cloud',
      '⛈️': 'cloud lightning rain',
      '🌤️': 'sun small cloud',
      '🌥️': 'sun behind large cloud',
      '🌦️': 'sun behind rain cloud',
      '🌧️': 'cloud rain',
      '🌨️': 'cloud snow',
      '🌩️': 'cloud lightning',
      '🌪️': 'tornado',
      '🌫️': 'fog',
      '🌬️': 'wind face',
      '🌀': 'cyclone',
      '🌈': 'rainbow',
      '🌂': 'closed umbrella',
      '☂️': 'umbrella',
      '☔': 'umbrella rain',
      '⛱️': 'umbrella ground',
      '⚡': 'high voltage',
      '❄️': 'snowflake',
      '☃️': 'snowman',
      '⛄': 'snowman without snow',
      '☄️': 'comet',
      '🔥': 'fire',
      '💧': 'droplet',
      '🌊': 'water wave',
      '🎃': 'jack o lantern',
      '🎄': 'christmas tree',
      '🎆': 'fireworks',
      '🎇': 'sparkler',
      '🧨': 'firecracker',
      '✨': 'sparkles',
      '🎈': 'balloon',
      '🎉': 'party popper',
      '🎊': 'confetti ball',
      '🎋': 'tanabata tree',
      '🎍': 'pine decoration',
      '🎎': 'japanese dolls',
      '🎏': 'carp streamer',
      '🎐': 'wind chime',
      '🎑': 'moon viewing ceremony',
      '🧧': 'red envelope',
      '🎀': 'ribbon',
      '🎁': 'wrapped gift',
      '🎗️': 'reminder ribbon',
      '🎟️': 'admission tickets',
      '🎫': 'ticket',
      '🎖️': 'military medal',
      '🏆': 'trophy',
      '🏅': 'sports medal',
      '🥇': 'first place medal',
      '🥈': 'second place medal',
      '🥉': 'third place medal',
      '⚽': 'soccer ball',
      '⚾': 'baseball',
      '🥎': 'softball',
      '🏀': 'basketball',
      '🏐': 'volleyball',
      '🏈': 'american football',
      '🏉': 'rugby football',
      '🎾': 'tennis',
      '🥏': 'flying disc',
      '🎳': 'bowling',
      '🏏': 'cricket game',
      '🏑': 'field hockey',
      '🏒': 'ice hockey',
      '🥍': 'lacrosse',
      '🏓': 'ping pong',
      '🏸': 'badminton',
      '🥊': 'boxing glove',
      '🥋': 'martial arts uniform',
      '🥅': 'goal net',
      '⛳': 'flag in hole',
      '⛸️': 'ice skate',
      '🎣': 'fishing pole',
      '🤿': 'diving mask',
      '🎽': 'running shirt',
      '🎿': 'skis',
      '🛷': 'sled',
      '🥌': 'curling stone',
      '🎯': 'direct hit',
      '🪀': 'yoyo',
      '🪁': 'kite',
      '🎱': 'pool 8 ball',
      '🔮': 'crystal ball',
      '🪄': 'magic wand',
      '🧿': 'nazar amulet',
      '🎮': 'video game',
      '🕹️': 'joystick',
      '🎰': 'slot machine',
      '🎲': 'game die',
      '🧩': 'puzzle piece',
      '🧸': 'teddy bear',
      '🪅': 'pinata',
      '🪆': 'nesting dolls',
      '♠️': 'spade suit',
      '♥️': 'heart suit',
      '♦️': 'diamond suit',
      '♣️': 'club suit',
      '♟️': 'chess pawn',
      '🃏': 'joker',
      '🀄': 'mahjong red dragon',
      '🎴': 'flower playing cards',
      '🎭': 'performing arts',
      '🖼️': 'framed picture',
      '🎨': 'artist palette',
      '🧵': 'thread',
      '🪡': 'sewing needle',
      '🧶': 'yarn',
      '🪢': 'knot',
      '🥽': 'goggles',
      '🥼': 'lab coat',
      '🦺': 'safety vest',
      '👔': 'necktie',
      '👕': 'tshirt',
      '👖': 'jeans',
      '🧣': 'scarf',
      '🧤': 'gloves',
      '🧥': 'coat',
      '🧦': 'socks',
      '👗': 'dress',
      '👘': 'kimono',
      '🥻': 'sari',
      '🩱': 'one piece swimsuit',
      '🩲': 'briefs',
      '🩳': 'shorts',
      '👙': 'bikini',
      '👚': 'womans clothes',
      '👛': 'purse',
      '👜': 'handbag',
      '👝': 'clutch bag',
      '🛍️': 'shopping bags',
      '🎒': 'backpack',
      '🩴': 'thong sandal',
      '👞': 'mans shoe',
      '👟': 'running shoe',
      '🥾': 'hiking boot',
      '🥿': 'flat shoe',
      '👠': 'high heeled shoe',
      '👡': 'womans sandal',
      '🩰': 'ballet shoes',
      '👢': 'womans boot',
      '👑': 'crown',
      '👒': 'womans hat',
      '🎩': 'top hat',
      '🎓': 'graduation cap',
      '🧢': 'billed cap',
      '🪖': 'military helmet',
      '⛑️': 'rescue workers helmet',
      '📿': 'prayer beads',
      '💄': 'lipstick',
      '💍': 'ring',
      '💎': 'gem stone',
      '🔇': 'muted speaker',
      '🔈': 'speaker low volume',
      '🔉': 'speaker medium volume',
      '🔊': 'speaker high volume',
      '📢': 'loudspeaker',
      '📣': 'megaphone',
      '📯': 'postal horn',
      '🔔': 'bell',
      '🔕': 'bell slash',
      '🎼': 'musical score',
      '🎵': 'musical note',
      '🎶': 'musical notes',
      '🎙️': 'studio microphone',
      '🎚️': 'level slider',
      '🎛️': 'control knobs',
      '🎤': 'microphone',
      '🎧': 'headphone',
      '📻': 'radio',
      '🎷': 'saxophone',
      '🪗': 'accordion',
      '🎸': 'guitar',
      '🎹': 'musical keyboard',
      '🎺': 'trumpet',
      '🎻': 'violin',
      '🪕': 'banjo',
      '🥁': 'drum',
      '🪘': 'long drum',
      '📱': 'mobile phone',
      '📲': 'mobile phone arrow',
      '☎️': 'telephone',
      '📞': 'telephone receiver',
      '📟': 'pager',
      '📠': 'fax machine',
      '🔋': 'battery',
      '🔌': 'electric plug',
      '💻': 'laptop',
      '🖥️': 'desktop computer',
      '🖨️': 'printer',
      '⌨️': 'keyboard',
      '🖱️': 'computer mouse',
      '🖲️': 'trackball',
      '💽': 'computer disk',
      '💾': 'floppy disk',
      '💿': 'optical disk',
      '📀': 'dvd',
      '🧮': 'abacus',
      '🎥': 'movie camera',
      '🎞️': 'film frames',
      '📽️': 'film projector',
      '🎬': 'clapper board',
      '📺': 'television',
      '📷': 'camera',
      '📸': 'camera flash',
      '📹': 'video camera',
      '📼': 'videocassette',
      '🔍': 'magnifying glass tilted left',
      '🔎': 'magnifying glass tilted right',
      '🕯️': 'candle',
      '💡': 'light bulb',
      '🔦': 'flashlight',
      '🏮': 'red paper lantern',
      '🪔': 'diya lamp',
      '📔': 'notebook decorative cover',
      '📕': 'closed book',
      '📖': 'open book',
      '📗': 'green book',
      '📘': 'blue book',
      '📙': 'orange book',
      '📚': 'books',
      '📓': 'notebook',
      '📒': 'ledger',
      '📃': 'page curl',
      '📜': 'scroll',
      '📄': 'page facing up',
      '📰': 'newspaper',
      '🗞️': 'rolled newspaper',
      '📑': 'bookmark tabs',
      '🔖': 'bookmark',
      '🏷️': 'label',
      '💰': 'money bag',
      '🪙': 'coin',
      '💴': 'yen banknote',
      '💵': 'dollar banknote',
      '💶': 'euro banknote',
      '💷': 'pound banknote',
      '💸': 'money wings',
      '💳': 'credit card',
      '🧾': 'receipt',
      '💹': 'chart increasing yen',
      '✉️': 'envelope',
      '📧': 'email',
      '📨': 'incoming envelope',
      '📩': 'envelope arrow',
      '📤': 'outbox tray',
      '📥': 'inbox tray',
      '📦': 'package',
      '📫': 'closed mailbox flag',
      '📪': 'closed mailbox lowered',
      '📬': 'open mailbox flag',
      '📭': 'open mailbox lowered',
      '📮': 'postbox',
      '🗳️': 'ballot box',
      '✏️': 'pencil',
      '✒️': 'black nib',
      '🖋️': 'fountain pen',
      '🖊️': 'pen',
      '🖌️': 'paintbrush',
      '🖍️': 'crayon',
      '📝': 'memo',
      '💼': 'briefcase',
      '📁': 'file folder',
      '📂': 'open file folder',
      '🗂️': 'card index dividers',
      '📅': 'calendar',
      '📆': 'tear off calendar',
      '🗒️': 'spiral notepad',
      '🗓️': 'spiral calendar',
      '📇': 'card index',
      '📈': 'chart increasing',
      '📉': 'chart decreasing',
      '📊': 'bar chart',
      '📋': 'clipboard',
      '📌': 'pushpin',
      '📍': 'round pushpin',
      '📎': 'paperclip',
      '🖇️': 'linked paperclips',
      '📏': 'straight ruler',
      '📐': 'triangular ruler',
      '✂️': 'scissors',
      '🗃️': 'card file box',
      '🗄️': 'file cabinet',
      '🗑️': 'wastebasket',
      '🔒': 'locked',
      '🔓': 'unlocked',
      '🔏': 'locked pen',
      '🔐': 'locked key',
      '🔑': 'key',
      '🗝️': 'old key',
      '🔨': 'hammer',
      '🪓': 'axe',
      '⛏️': 'pick',
      '⚒️': 'hammer pick',
      '🛠️': 'hammer wrench',
      '🗡️': 'dagger',
      '⚔️': 'crossed swords',
      '🔫': 'pistol',
      '🪃': 'boomerang',
      '🏹': 'bow arrow',
      '🛡️': 'shield',
      '🪚': 'carpentry saw',
      '🔧': 'wrench',
      '🪛': 'screwdriver',
      '🔩': 'nut bolt',
      '⚙️': 'gear',
      '🗜️': 'clamp',
      '⚖️': 'balance scale',
      '🦯': 'probing cane',
      '🔗': 'link',
      '⛓️': 'chains',
      '🪝': 'hook',
      '🧰': 'toolbox',
      '🧲': 'magnet',
      '🪜': 'ladder',
      '⚗️': 'alembic',
      '🧪': 'test tube',
      '🧫': 'petri dish',
      '🧬': 'dna',
      '🔬': 'microscope',
      '🔭': 'telescope',
      '📡': 'satellite antenna',
      '💉': 'syringe',
      '🩸': 'drop blood',
      '💊': 'pill',
      '🩹': 'adhesive bandage',
      '🩺': 'stethoscope',
      '🚪': 'door',
      '🛗': 'elevator',
      '🪞': 'mirror',
      '🪟': 'window',
      '🛏️': 'bed',
      '🛋️': 'couch lamp',
      '🪑': 'chair',
      '🚽': 'toilet',
      '🪠': 'plunger',
      '🚿': 'shower',
      '🛁': 'bathtub',
      '🪤': 'mouse trap',
      '🪒': 'razor',
      '🧴': 'lotion bottle',
      '🧷': 'safety pin',
      '🧹': 'broom',
      '🧺': 'basket',
      '🧻': 'roll paper',
      '🪣': 'bucket',
      '🧼': 'soap',
      '🪥': 'toothbrush',
      '🧽': 'sponge',
      '🧯': 'fire extinguisher',
      '🛒': 'shopping cart',
      '🚬': 'cigarette',
      '⚰️': 'coffin',
      '🪦': 'headstone',
      '⚱️': 'funeral urn',
      '🗿': 'moai',
      '🪧': 'placard',
      '🚩': 'triangular flag',
      '🏳️': 'white flag',
      '🏴': 'black flag',
      '🏳️‍🌈': 'rainbow flag',
      '🏳️‍⚧️': 'transgender flag',
      '🏴‍☠️': 'pirate flag',
      '💯': 'hundred points',
      '✅': 'check mark button',
      '☑️': 'check box check',
      '✔️': 'check mark',
      '❌': 'cross mark',
      '❎': 'cross mark button',
      '➕': 'plus',
      '➖': 'minus',
      '➗': 'divide',
      '✖️': 'multiply',
      '♾️': 'infinity',
      '‼️': 'double exclamation',
      '⁉️': 'exclamation question',
      '❓': 'question mark',
      '❔': 'white question mark',
      '❕': 'white exclamation',
      '❗': 'exclamation mark',
      '〰️': 'wavy dash',
      '💱': 'currency exchange',
      '💲': 'heavy dollar sign',
      '⚕️': 'medical symbol',
      '♻️': 'recycling symbol',
      '⚜️': 'fleur de lis',
      '🔱': 'trident emblem',
      '📛': 'name badge',
      '🔰': 'japanese symbol beginner',
      '⭕': 'hollow red circle',
      '✅': 'white check mark',
      '☑️': 'check box check',
      '✔️': 'check mark',
      '❌': 'cross mark',
      '❎': 'cross mark button',
      '➰': 'curly loop',
      '➿': 'double curly loop',
      '〽️': 'part alternation mark',
      '✳️': 'eight spoked asterisk',
      '✴️': 'eight pointed star',
      '❇️': 'sparkle',
      '©️': 'copyright',
      '®️': 'registered',
      '™️': 'trade mark',
      '#️⃣': 'keycap number',
      '*️⃣': 'keycap asterisk',
      '0️⃣': 'keycap zero',
      '1️⃣': 'keycap one',
      '2️⃣': 'keycap two',
      '3️⃣': 'keycap three',
      '4️⃣': 'keycap four',
      '5️⃣': 'keycap five',
      '6️⃣': 'keycap six',
      '7️⃣': 'keycap seven',
      '8️⃣': 'keycap eight',
      '9️⃣': 'keycap nine',
      '🔟': 'keycap ten',
      '🔠': 'input latin uppercase',
      '🔡': 'input latin lowercase',
      '🔢': 'input numbers',
      '🔣': 'input symbols',
      '🔤': 'input latin letters',
      '🅰️': 'a button blood type',
      '🆎': 'ab button blood type',
      '🅱️': 'b button blood type',
      '🆑': 'cl button',
      '🆒': 'cool button',
      '🆓': 'free button',
      'ℹ️': 'information',
      '🆔': 'id button',
      'Ⓜ️': 'circled m',
      '🆕': 'new button',
      '🆖': 'ng button',
      '🅾️': 'o button blood type',
      '🆗': 'ok button',
      '🅿️': 'p button',
      '🆘': 'sos button',
      '🆙': 'up button',
      '🆚': 'vs button',
      '🈁': 'japanese here button',
      '🈂️': 'japanese service charge',
      '🈷️': 'japanese monthly amount',
      '🈶': 'japanese not free charge',
      '🈯': 'japanese reserved',
      '🉐': 'japanese bargain',
      '🈹': 'japanese discount',
      '🈚': 'japanese free charge',
      '🈲': 'japanese prohibited',
      '🉑': 'japanese acceptable',
      '🈸': 'japanese application',
      '🈴': 'japanese passing grade',
      '🈳': 'japanese vacancy',
      '㊗️': 'japanese congratulations',
      '㊙️': 'japanese secret',
      '🈺': 'japanese open business',
      '🈵': 'japanese no vacancy',
      '🔴': 'red circle',
      '🟠': 'orange circle',
      '🟡': 'yellow circle',
      '🟢': 'green circle',
      '🔵': 'blue circle',
      '🟣': 'purple circle',
      '🟤': 'brown circle',
      '⚫': 'black circle',
      '⚪': 'white circle',
      '🟥': 'red square',
      '🟧': 'orange square',
      '🟨': 'yellow square',
      '🟩': 'green square',
      '🟦': 'blue square',
      '🟪': 'purple square',
      '🟫': 'brown square',
      '⬛': 'black large square',
      '⬜': 'white large square',
      '◼️': 'black medium square',
      '◻️': 'white medium square',
      '◾': 'black medium small square',
      '◽': 'white medium small square',
      '▪️': 'black small square',
      '▫️': 'white small square',
      '🔶': 'large orange diamond',
      '🔷': 'large blue diamond',
      '🔸': 'small orange diamond',
      '🔹': 'small blue diamond',
      '🔺': 'red triangle pointed up',
      '🔻': 'red triangle pointed down',
      '💠': 'diamond shape dot',
      '🔘': 'radio button',
      '🔳': 'white square button',
      '🔲': 'black square button'
    };
    
    return emojiNames[emoji] || emoji;
  };

  const insertVideo = () => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const value = prompt('Enter video URL:');
    if (value) {
      const range = quill.getSelection();
      quill.insertEmbed(range.index, 'video', value);
    }
  };

  const handleButtonInsert = (buttonData) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    
    if (editingButtonNode) {
      // Update existing button in place
      const ButtonBlot = Quill.import('formats/button');
      ButtonBlot.update(editingButtonNode, buttonData);
      
      // Update the blot's data
      const blot = Quill.find(editingButtonNode);
      if (blot) {
        blot.domNode.setAttribute('data-button-config', JSON.stringify(buttonData));
      }
      
      // Reset editing state
      setEditingButton(null);
      setEditingButtonNode(null);
      
      // Trigger a content change to ensure Quill tracks the update
      quill.update('user');
    } else {
      // Insert new button
      const range = quill.getSelection(true);
      
      if (range) {
        // Insert a newline if we're not at the beginning of a line
        const [line] = quill.getLine(range.index);
        if (line && line.length() > 0) {
          quill.insertText(range.index, '\n');
          quill.insertEmbed(range.index + 1, 'button', buttonData);
          quill.insertText(range.index + 2, '\n');
          quill.setSelection(range.index + 3);
        } else {
          // Insert at current position
          quill.insertEmbed(range.index, 'button', buttonData);
          quill.insertText(range.index + 1, '\n');
          quill.setSelection(range.index + 2);
        }
      } else {
        // No selection, append at the end
        const length = quill.getLength();
        quill.insertEmbed(length - 1, 'button', buttonData);
        quill.insertText(length, '\n');
        quill.setSelection(length + 1);
      }
    }
    
    // Focus back on editor
    quill.focus();
  };

  const handleInputInsert = (inputData) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    
    if (editingInputNode) {
      // Update existing input in place
      const InputBlot = Quill.import('formats/input');
      InputBlot.update(editingInputNode, inputData);
      
      // Update the blot's data
      const blot = Quill.find(editingInputNode);
      if (blot) {
        const wrapper = blot.domNode.querySelector('.input-wrapper');
        if (wrapper) {
          wrapper.setAttribute('data-input-config', JSON.stringify(inputData));
        }
      }
      
      // Reset editing state
      setEditingInput(null);
      setEditingInputNode(null);
      
      // Trigger a content change to ensure Quill tracks the update
      quill.update('user');
    } else {
      // Insert new input
      const range = quill.getSelection(true);
      
      if (range) {
        // Insert a newline if we're not at the beginning of a line
        const [line] = quill.getLine(range.index);
        if (line && line.length() > 0) {
          quill.insertText(range.index, '\n');
          quill.insertEmbed(range.index + 1, 'input', inputData);
          quill.insertText(range.index + 2, '\n');
          quill.setSelection(range.index + 3);
        } else {
          // Insert at current position
          quill.insertEmbed(range.index, 'input', inputData);
          quill.insertText(range.index + 1, '\n');
          quill.setSelection(range.index + 2);
        }
      } else {
        // No selection, append at the end
        const length = quill.getLength();
        quill.insertEmbed(length - 1, 'input', inputData);
        quill.insertText(length, '\n');
        quill.setSelection(length + 1);
      }
    }
    
    // Focus back on editor
    quill.focus();
  };

  return (
    <>
      <Box
      id="toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: theme.palette.background.paper,
        px: { xs: 0.5, sm: 0.75, md: 1 },
        py: { xs: 0.25, sm: 0.35, md: 0.4 },
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '100%',
        flexWrap: 'nowrap',
        // Single spacing system using gap only
        gap: { 
          xs: '4px',   // Mobile
          sm: '6px',   // Tablet  
          md: '8px',   // Desktop
          lg: '10px'   // Large
        },
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.divider, 0.3),
          borderRadius: 2,
        },
        // Ensure all children don't shrink
        '& > *': {
          flexShrink: 0,
        },
      }}>
        {/* Text Format Dropdowns */}
        <ToolbarGroup>
        <FormControl size="small" sx={{ minWidth: { xs: 65, sm: 70, md: 75 }, maxWidth: { xs: 85, sm: 90, md: 95 } }}>
          <Select
            value={currentFormats.header || '0'}
            onChange={(e) => handleHeaderChange(e.target.value)}
            sx={{ 
              height: { xs: 22, sm: 24, md: 26 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              // backgroundColor: theme.palette.background.default,
              // '& .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.divider,
              // },
              // '&:hover .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.text.primary,
              // },
              // '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.primary.main,
              // },
              borderRadius:0.5,
            }}
          >
            <MenuItem value="0">Normal</MenuItem>
            <MenuItem value="1">Header 1</MenuItem>
            <MenuItem value="2">Header 2</MenuItem>
            <MenuItem value="3">Header 3</MenuItem>
            <MenuItem value="4">Header 4</MenuItem>
            <MenuItem value="5">Header 5</MenuItem>
            <MenuItem value="6">Header 6</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 75, sm: 80, md: 85 }, maxWidth: { xs: 95, sm: 100, md: 105 } }}>
          <Select
            value={currentFormats.font || 'default'}
            onChange={(e) => handleFontChange(e.target.value)}
            sx={{ 
              height: { xs: 22, sm: 24, md: 26 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              // backgroundColor: theme.palette.background.default,
              // '& .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.divider,
              // },
              // '&:hover .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.text.primary,
              // },
              // '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.primary.main,
              // },
                borderRadius:0.5,
            }}
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="serif">Serif</MenuItem>
            <MenuItem value="monospace">Monospace</MenuItem>
            <MenuItem value="cairo">Cairo</MenuItem>
            <MenuItem value="roboto">Roboto</MenuItem>
            <MenuItem value="opensans">Open Sans</MenuItem>
            <MenuItem value="lato">Lato</MenuItem>
            <MenuItem value="montserrat">Montserrat</MenuItem>
            <MenuItem value="poppins">Poppins</MenuItem>
            <MenuItem value="tajawal">Tajawal</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 85, md: 90 }, maxWidth: { xs: 100, sm: 105, md: 110 } }}>
          <Select
            value={String(currentFormats.weight || '400')}
            onChange={(e) => handleWeightChange(e.target.value)}
            sx={{ 
              height: { xs: 22, sm: 24, md: 26 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              borderRadius: 0.5,
            }}
          >
            <MenuItem value="100">Thin (100)</MenuItem>
            <MenuItem value="300">Light (300)</MenuItem>
            <MenuItem value="400">Normal (400)</MenuItem>
            <MenuItem value="500">Medium (500)</MenuItem>
            <MenuItem value="600">Semibold (600)</MenuItem>
            <MenuItem value="700">Bold (700)</MenuItem>
            <MenuItem value="800">Extra Bold (800)</MenuItem>
            <MenuItem value="900">Black (900)</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 55, sm: 60, md: 65 }, maxWidth: { xs: 70, sm: 75, md: 80 } }}>
          <Select
            value={currentFormats.size || 'default'}
            onChange={(e) => handleSizeChange(e.target.value)}
            sx={{ 
              height: { xs: 22, sm: 24, md: 26 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              // backgroundColor: theme.palette.background.default,
              // '& .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.divider,
              // },
              // '&:hover .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.text.primary,
              // },
              // '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              //   borderColor: theme.palette.primary.main,
              // },
                borderRadius:0.5
            }}
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="10px">10px</MenuItem>
            <MenuItem value="12px">12px</MenuItem>
            <MenuItem value="14px">14px</MenuItem>
            <MenuItem value="16px">16px</MenuItem>
            <MenuItem value="18px">18px</MenuItem>
            <MenuItem value="20px">20px</MenuItem>
            <MenuItem value="24px">24px</MenuItem>
            <MenuItem value="28px">28px</MenuItem>
            <MenuItem value="32px">32px</MenuItem>
            <MenuItem value="36px">36px</MenuItem>
            <MenuItem value="48px">48px</MenuItem>
            <MenuItem value="64px">64px</MenuItem>
          </Select>
        </FormControl>
      </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            icon={<FormatBold />}
            tooltip="Bold"
            onClick={() => applyFormat('bold')}
            active={currentFormats.bold}
          />
          <ToolbarButton
            icon={<FormatItalic />}
            tooltip="Italic"
            onClick={() => applyFormat('italic')}
            active={currentFormats.italic}
          />
          <ToolbarButton
            icon={<FormatUnderlined />}
            tooltip="Underline"
            onClick={() => applyFormat('underline')}
            active={currentFormats.underline}
          />
          <ToolbarButton
            icon={<FormatStrikethrough />}
            tooltip="Strikethrough"
            onClick={() => applyFormat('strike')}
            active={currentFormats.strike}
          />
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ColorPicker
            icon={<FormatColorText />}
            tooltip="Text Color"
            value={currentFormats.color}
            onChange={applyFormat}
            type="color"
          />
          <ColorPicker
            icon={<FormatColorFill />}
            tooltip="Background Color"
            value={currentFormats.background}
            onChange={applyFormat}
            type="background"
          />
          <ToolbarButton
            icon={<Subscript />}
            tooltip="Subscript"
            onClick={() => applyFormat('script', 'sub')}
            active={currentFormats.script === 'sub'}
          />
          <ToolbarButton
            icon={<Superscript />}
            tooltip="Superscript"
            onClick={() => applyFormat('script', 'super')}
            active={currentFormats.script === 'super'}
          />
        </ToolbarGroup>

        <ToolbarDivider />
        
        <ToolbarGroup>
          <ToolbarButton
            icon={<FormatClear />}
            tooltip="Clear Formatting"
            onClick={() => {
              if (!quillRef.current) return;
              const quill = quillRef.current.getEditor();
              quill.removeFormat(quill.getSelection());
            }}
          />
        </ToolbarGroup>

        {/* Lists and Indentation */}
        <ToolbarDivider />
        <ToolbarGroup>
          <ToolbarButton
            icon={<FormatListBulleted />}
            tooltip="Bullet List"
            onClick={() => applyFormat('list', 'bullet')}
            active={currentFormats.list === 'bullet'}
          />
          <ToolbarButton
            icon={<FormatListNumbered />}
            tooltip="Numbered List"
            onClick={() => applyFormat('list', 'ordered')}
            active={currentFormats.list === 'ordered'}
          />
          <ToolbarButton
            icon={<FormatIndentDecrease />}
            tooltip="Decrease Indent"
            onClick={() => applyFormat('indent', '-1')}
          />
          <ToolbarButton
            icon={<FormatIndentIncrease />}
            tooltip="Increase Indent"
            onClick={() => applyFormat('indent', '+1')}
          />
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            icon={<FormatAlignLeft />}
            tooltip="Align Left"
            onClick={() => handleAlignChange('left')}
            active={!currentFormats.align || currentFormats.align === 'left'}
          />
          <ToolbarButton
            icon={<FormatAlignCenter />}
            tooltip="Align Center"
            onClick={() => handleAlignChange('center')}
            active={currentFormats.align === 'center'}
          />
          <ToolbarButton
            icon={<FormatAlignRight />}
            tooltip="Align Right"
            onClick={() => handleAlignChange('right')}
            active={currentFormats.align === 'right'}
          />
          <ToolbarButton
            icon={<FormatAlignJustify />}
            tooltip="Justify"
            onClick={() => handleAlignChange('justify')}
            active={currentFormats.align === 'justify'}
          />
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            icon={<FormatQuote />}
            tooltip="Blockquote"
            onClick={() => applyFormat('blockquote')}
            active={currentFormats.blockquote}
          />
          <ToolbarButton
            icon={<Code />}
            tooltip="Code Block"
            onClick={() => applyFormat('code-block')}
            active={currentFormats['code-block']}
          />
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            icon={<Link />}
            tooltip="Insert Link"
            onClick={insertLink}
          />
          <ToolbarButton
            icon={<Image />}
            tooltip="Insert Image"
            onClick={(e) => setImageMenuAnchor(e.currentTarget)}
          />
          <ToolbarButton
            icon={<VideoLibrary />}
            tooltip="Insert Video"
            onClick={insertVideo}
          />
          <ToolbarButton
            icon={<EmojiIcon />}
            tooltip="Insert Emoji"
            onClick={(e) => {
              // Store current selection before opening menu
              if (quillRef.current) {
                const quill = quillRef.current.getEditor();
                const selection = quill.getSelection();
                if (selection) {
                  setLastSelection(selection);
                }
              }
              setEmojiMenuAnchor(e.currentTarget);
            }}
          />
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            icon={<PageBreakIcon />}
            tooltip="Insert Page Break"
            onClick={insertPageBreak}
          />
          <ToolbarButton
            icon={<SmartButton />}
            tooltip="Insert Button"
            onClick={() => setButtonDialogOpen(true)}
          />
          {pdfType === 'interactive' && (
            <ToolbarButton
              icon={<Input />}
              tooltip="Insert Input Field"
              onClick={() => setInputDialogOpen(true)}
            />
          )}
        </ToolbarGroup>
    </Box>
    
    <ButtonDialog
      open={buttonDialogOpen}
      onClose={() => {
        setButtonDialogOpen(false);
        setEditingButton(null);
        setEditingButtonNode(null);
      }}
      onInsert={handleButtonInsert}
      initialData={editingButton}
    />
    
    <InputDialog
      open={inputDialogOpen}
      onClose={() => {
        setInputDialogOpen(false);
        setEditingInput(null);
        setEditingInputNode(null);
      }}
      onInsert={handleInputInsert}
      initialData={editingInput}
    />
    
    {/* Image Menu */}
    <Menu
      anchorEl={imageMenuAnchor}
      open={Boolean(imageMenuAnchor)}
      onClose={() => setImageMenuAnchor(null)}
    >
      <MenuItem onClick={insertImage}>
        <ListItemIcon>
          <Link fontSize="small" />
        </ListItemIcon>
        <ListItemText>From URL</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => fileInputRef.current?.click()}>
        <ListItemIcon>
          <UploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Upload from Computer</ListItemText>
      </MenuItem>
    </Menu>
    
    {/* Emoji Picker Menu */}
    <Menu
      anchorEl={emojiMenuAnchor}
      open={Boolean(emojiMenuAnchor)}
      onClose={() => {
        setEmojiMenuAnchor(null);
        setEmojiSearch('');
        setEmojiTab(0);
      }}
      PaperProps={{
        style: {
          maxHeight: 400,
          width: 380,
        },
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Search bar */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search emojis..."
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: emojiSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setEmojiSearch('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {/* Category tabs */}
        <Tabs
          value={emojiTab}
          onChange={(e, newValue) => setEmojiTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
        >
          <Tab label="Recent" sx={{ minHeight: 40, minWidth: 60 }} />
          <Tab label="😊" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="👋" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="🐶" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="🍎" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="⚽" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="🚗" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="💡" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="❤️" sx={{ minHeight: 40, minWidth: 40 }} />
          <Tab label="🏳️" sx={{ minHeight: 40, minWidth: 40 }} />
        </Tabs>
        
        {/* Emoji grid */}
        <Box sx={{ 
          p: 1, 
          height: 250, 
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {(() => {
            const categoryKeys = Object.keys(emojiCategories);
            const currentCategory = emojiCategories[categoryKeys[emojiTab]];
            const filteredEmojis = getFilteredEmojis(currentCategory);
            
            if (filteredEmojis.length === 0) {
              return (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  {emojiTab === 0 && recentEmojis.length === 0 
                    ? 'No recent emojis' 
                    : 'No emojis found'}
                </Box>
              );
            }
            
            return (
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
                gap: 0.5,
                width: '100%'
              }}>
                {filteredEmojis.map((emoji, index) => (
                  <Box
                    key={index}
                    onClick={() => insertEmoji(emoji)}
                    sx={{
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '22px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      aspectRatio: '1',
                      minWidth: 0,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {emoji}
                  </Box>
                ))}
              </Box>
            );
          })()}
        </Box>
      </Box>
    </Menu>
    
    {/* Hidden file input */}
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={handleImageUpload}
    />
    </>
  );
};

export default CustomQuillToolbar;